import { createPreviouslySelectedOptionsForItems } from '../../../../reducers/student/tracking/questionWidgetTrackingReducer';
import { CommaSerializer } from '../serializer/Serializer';
import {
  CourseCompletionStatus,
  IPersistenceTrackingClient,
  PersistenceTrackingData,
  QuizStatus,
  TrackingDataVersion,
} from '../trackingClient';
import { ScormClient, scormClient } from './scormTyped';
import Immutable from 'immutable';

async function _delay(delayTimeMs: number) {
  return new Promise((r) => setTimeout(r, delayTimeMs));
}

export class ScormTrackingClient implements IPersistenceTrackingClient {
  private _scormClient: ScormClient;
  private _replaceQuoteWithDollarSign = (jsonString: string) => jsonString.replaceAll('"', '$');
  private _replaceDollarSignWithQuote = (jsonString: string) => jsonString.replaceAll('$', '"');

  constructor() {
    window.addEventListener('beforeunload', (e) => {
      if (this._scormClient.connection.isActive) {
        e.preventDefault();
        return void this.terminateConnection().then(() => (e.returnValue = 'terminate'));
      }
      return;
    });
  }

  name = 'SCORM';
  doesSupportLegacyData = false;

  init = async (): Promise<boolean> => {
    let connected = this._init();

    let timeout = 1000;
    let retryCount = 0;
    while (!connected) {
      await _delay(timeout);
      timeout *= 2;
      connected = this._init();
      retryCount = retryCount + 1;
      if (retryCount === 4) {
        throw Error('Scorm cannot be initialized.');
      }
    }

    return connected;
  };

  getTrackingData = (): Promise<PersistenceTrackingData> => {
    const suspendData = this._scormClient.get('cmi.suspend_data');
    if (suspendData === 'null' || suspendData === '') {
      // suspend_data returns null as "null" string which causes problems with mapToTrackingData
      const emptyTrackingData: PersistenceTrackingData = {
        widgetTrackingData: createPreviouslySelectedOptionsForItems([]),
        finishedItems: Immutable.Set(),
        version: TrackingDataVersion.v2,
      };
      return Promise.resolve(emptyTrackingData);
    }

    const score = this._scormClient.get('cmi.score.raw');
    const successStatus = this._scormClient.get('cmi.success_status');

    const quizStatus: QuizStatus = {
      score: parseInt(score, 10) ?? null,
      passed: successStatus === 'passed' ? true : successStatus === 'failed' ? false : null,
    };

    const trackingData = CommaSerializer.deserializeData(this._replaceDollarSignWithQuote(suspendData));
    trackingData.quizStatus = quizStatus;
    return Promise.resolve(trackingData);
  };

  async afterSetup(): Promise<void> {
    await this.setCompletionStatus(CourseCompletionStatus.Incomplete);
  }

  setTrackingData = ({ widgetTrackingData, finishedItems }: PersistenceTrackingData): Promise<void> => {
    this._scormClient.set(
      'cmi.suspend_data',
      this._replaceQuoteWithDollarSign(CommaSerializer.serializeData(finishedItems, widgetTrackingData))
    );
    setTimeout(() => {
      this._scormClient.save();
    }, 500);
    return Promise.resolve();
  };

  setQuestinInteraction = (): Promise<void> => {
    return Promise.resolve();
  };

  setQuizStatus = (quizStatus: QuizStatus): Promise<void> => {
    if (quizStatus.score !== null) {
      this._scormClient.set('cmi.score.raw', `${quizStatus.score}`);
      this._scormClient.set('cmi.score.scaled', `${quizStatus.score / 100}`);
      this._scormClient.set('cmi.score.max', '100');
      this._scormClient.set('cmi.score.min', '0');
      if (quizStatus.passed) {
        this._scormClient.set('cmi.success_status', 'passed');
      } else {
        this._scormClient.set('cmi.success_status', 'failed');
      }
      setTimeout(() => {
        this._scormClient.save();
      }, 500);
    }
    return Promise.resolve();
  };

  setCompletionStatus = (newStatus: CourseCompletionStatus): Promise<void> => {
    const currentStatus: CourseCompletionStatus = this._scormClient.status('get');
    if (!(currentStatus === CourseCompletionStatus.Completed || currentStatus === newStatus)) {
      this._scormClient.status('set', newStatus);
      setTimeout(() => {
        this._scormClient.save();
      }, 500);
    }
    return Promise.resolve();
  };

  terminateConnection = (): Promise<void> => {
    this._scormClient.quit();
    return Promise.resolve();
  };

  private _init = (): boolean => {
    this._scormClient = scormClient;
    this._scormClient.init();
    return this._scormClient.connection.isActive
  };

}
